<template>
  <div class="dash-root">
    <!-- ═══ Hero Header ════════════════════════════════════════ -->
    <div class="dash-hero">
      <div class="dash-hero__bg"></div>
      <div class="dash-hero__inner">
        <div class="dash-hero__brand">
          <div class="dash-hero__icon">
            <i class="fas fa-credit-card"></i>
          </div>
          <div>
            <h1 class="dash-hero__title">Top‑ups</h1>
            <p class="dash-hero__subtitle">Manage, monitor &amp; communicate</p>
          </div>
        </div>
        <div class="dash-hero__actions">
          <button class="btn btn--templates-manager" @click="openTemplateManager">
            <i class="fas fa-layer-group"></i>
            <span class="btn__text">Templates</span>
          </button>
          <button class="btn btn--primary" @click="goCreate">
            <i class="fas fa-plus"></i>
            <span class="btn__text">Create TopUp</span>
          </button>
          <button class="btn btn--soft" @click="goRecharge">
            <i class="fas fa-sync-alt"></i>
            <span class="btn__text">Recharge</span>
          </button>
        </div>
      </div>

      <!-- Zone bar -->
      <div class="zone-bar">
        <div class="zone-bar__inner">
          <div class="zone-picker">
            <i class="fas fa-map-marker-alt zone-picker__icon"></i>
            <v-select
              v-model="selectedZoneFilter"
              :items="zone_types"
              item-value="id"
              item-text="info"
              label="Select Zone"
              outlined
              dense
              clearable
              hide-details
              class="zone-picker__select"
              :menu-props="{ offsetY: true, maxHeight: 320 }"
            ></v-select>
          </div>
          <div class="zone-divider"></div>
          <div class="date-picker">
            <i class="fas fa-calendar-alt date-picker__icon"></i>
            <input type="date" v-model="selectedStartDate" class="date-input" placeholder="Start" />
            <span class="date-sep">→</span>
            <input type="date" v-model="selectedEndDate" class="date-input" placeholder="End" />
          </div>
          <button class="btn btn--search" @click="fetchTopUps" :disabled="loading || !selectedZoneFilter">
            <i class="fas fa-search" :class="{ 'fa-spin': loading }"></i>
            <span class="btn__text">Search</span>
          </button>
        </div>
      </div>
    </div>

    <!-- ═══ Stats ═══════════════════════════════════════════════ -->
    <div class="stats-grid">
      <div class="stat-card" v-for="stat in statsCards" :key="stat.key">
        <div class="stat-card__icon" :style="{ background: stat.bg, color: stat.color }">
          <i :class="stat.icon"></i>
        </div>
        <div class="stat-card__body">
          <span class="stat-card__val">{{ stat.value }}</span>
          <span class="stat-card__lbl">{{ stat.label }}</span>
        </div>
        <div class="stat-card__bar" :style="{ background: stat.color, width: stat.percent + '%' }"></div>
      </div>
    </div>

    <!-- ═══ Toolbar ═══════════════════════════════════════════ -->
    <div class="table-wrap">
      <div class="table-toolbar">
        <div class="search-group">
          <i class="fas fa-search search-group__icon"></i>
          <input v-model="search" type="text" class="search-input" placeholder="Search by name…" />
          <button v-if="search" class="search-clear" @click="search = ''">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <span class="table-meta">
          <i class="fas fa-users"></i>
          {{ filteredTopUps.length }} top‑up{{ filteredTopUps.length === 1 ? '' : 's' }}
        </span>
      </div>

      <!-- ═══ DESKTOP TABLE (≥ 768px) ═══════════════════════ -->
      <div class="table-desktop" v-show="!isMobile">
        <v-data-table
          :headers="headers"
          :items="filteredTopUps"
          :loading="loading"
          class="topups-table elevation-0"
          item-key="id"
          :items-per-page="10"
          :footer-props="{ itemsPerPageOptions: [10, 25, 50] }"
          mobile-breakpoint="0"
        >
          <template v-slot:item.username="{ item }">
            <div class="user-cell">
              <div class="user-avatar">{{ item.username ? item.username.charAt(0).toUpperCase() : '?' }}</div>
              <div class="user-info">
                <div class="user-name">{{ item.username }}</div>
                <div class="user-zone">{{ item.topup_zone }}</div>
              </div>
            </div>
          </template>
          <template v-slot:item.amount="{ item }">
            <span class="amount-cell">{{ numberFormat(item.amount) }} FCFA</span>
          </template>
          <template v-slot:item.amount_deposited_campaign="{ item }">
            <span class="deposit-cell">{{ numberFormat(item.amount_deposited_campaign) }}</span>
          </template>
          <template v-slot:item.amount_deposited_loan="{ item }">
            <span class="deposit-cell">{{ numberFormat(item.amount_deposited_loan) }}</span>
          </template>
          <template v-slot:item.amount_withdrawn_campaign="{ item }">
            <span class="withdraw-cell">{{ numberFormat(item.amount_withdrawn_campaign) }}</span>
          </template>
          <template v-slot:item.status="{ item }">
            <v-chip small :color="statusColor[item.status] || 'grey'" class="font-weight-medium status-chip" label>
              {{ item.status }}
            </v-chip>
          </template>
          <template v-slot:item.date_time="{ item }">
            <span class="date-cell">{{ formatDate(item.date_time) }}</span>
          </template>
          <template v-slot:item.actions="{ item }">
            <div class="action-group">
              <button class="action-btn action-btn--view" title="View details" @click.stop="viewDetails(item.id)">
                <i class="fas fa-eye"></i>
              </button>
              <button class="action-btn action-btn--whatsapp" title="WhatsApp" @click.stop="openCommunication(item, 'whatsapp')">
                <i class="fab fa-whatsapp"></i>
              </button>
              <button class="action-btn action-btn--email" title="Email" @click.stop="openCommunication(item, 'email')">
                <i class="fas fa-envelope"></i>
              </button>
              <button class="action-btn action-btn--webpush" title="WebPush" @click.stop="openCommunication(item, 'webpush')">
                <i class="fas fa-bell"></i>
              </button>
              <button class="action-btn action-btn--chat" title="Chat" @click.stop="sendInAppMessage(item)">
                <i class="fas fa-comment-dots"></i>
              </button>
            </div>
          </template>
          <template v-slot:no-data>
            <div class="empty-table">
              <div class="empty-table__illustration"><i class="fas fa-credit-card"></i></div>
              <p>No top‑ups found</p>
              <small>Try adjusting your zone or date filters</small>
            </div>
          </template>
        </v-data-table>
      </div>

      <!-- ═══ MOBILE CARDS (< 768px) ══════════════════════════ -->
      <div class="mobile-cards" v-show="isMobile">
        <transition-group name="card-list" tag="div" class="mobile-cards__grid">
          <div v-for="item in filteredTopUps" :key="item.id" class="mobile-card">
            <div class="mobile-card__header">
              <div class="mobile-card__avatar">{{ item.username ? item.username.charAt(0).toUpperCase() : '?' }}</div>
              <div class="mobile-card__info">
                <div class="mobile-card__name">{{ item.username }}</div>
                <div class="mobile-card__zone"><i class="fas fa-map-marker-alt"></i> {{ item.topup_zone }}</div>
              </div>
              <v-chip x-small :color="statusColor[item.status] || 'grey'" class="font-weight-bold status-chip" label>
                {{ item.status }}
              </v-chip>
            </div>
            <div class="mobile-card__body">
              <div class="mobile-card__row">
                <span class="mobile-card__label">Principal</span>
                <span class="mobile-card__value mobile-card__value--primary">{{ numberFormat(item.amount) }} FCFA</span>
              </div>
              <div class="mobile-card__row">
                <span class="mobile-card__label">Loan Deposits</span>
                <span class="mobile-card__value">{{ numberFormat(item.amount_deposited_loan) }}</span>
              </div>
              <div class="mobile-card__row">
                <span class="mobile-card__label">Campaign Deposit</span>
                <span class="mobile-card__value">{{ numberFormat(item.amount_deposited_campaign) }}</span>
              </div>
              <div class="mobile-card__row">
                <span class="mobile-card__label">Withdrawals</span>
                <span class="mobile-card__value mobile-card__value--danger">{{ numberFormat(item.amount_withdrawn_campaign) }}</span>
              </div>
              <div class="mobile-card__row">
                <span class="mobile-card__label">Joined</span>
                <span class="mobile-card__value">{{ formatDate(item.date_time) }}</span>
              </div>
            </div>
            <div class="mobile-card__actions">
              <button class="mobile-action mobile-action--view" @click="viewDetails(item.id)">
                <i class="fas fa-eye"></i><span>Details</span>
              </button>
              <button class="mobile-action mobile-action--whatsapp" @click="openCommunication(item, 'whatsapp')">
                <i class="fab fa-whatsapp"></i><span>WhatsApp</span>
              </button>
              <button class="mobile-action mobile-action--email" @click="openCommunication(item, 'email')">
                <i class="fas fa-envelope"></i><span>Email</span>
              </button>
              <button class="mobile-action mobile-action--webpush" @click="openCommunication(item, 'webpush')">
                <i class="fas fa-bell"></i><span>WebPush</span>
              </button>
              <button class="mobile-action mobile-action--chat" @click="sendInAppMessage(item)">
                <i class="fas fa-comment-dots"></i><span>Chat</span>
              </button>
            </div>
          </div>
        </transition-group>
        <div v-if="!filteredTopUps.length && !loading" class="empty-table empty-table--mobile">
          <div class="empty-table__illustration"><i class="fas fa-credit-card"></i></div>
          <p>No top‑ups found</p>
          <small>Try adjusting your zone or date filters</small>
        </div>
      </div>
    </div>

    <!-- ═══ Modal Communication (email/whatsapp/webpush) ═══════════════ -->
    <transition name="modal">
      <div v-if="showModal" class="modal-overlay" @click.self="closeModal">
        <div class="modal-card modal-card--comm">
          <div class="modal-card__head">
            <div class="modal-card__brand">
              <span class="modal-card__icon" :class="'modal-card__icon--' + selectedChannel">
                <i :class="channelIcon(selectedChannel)"></i>
              </span>
              <div>
                <div class="modal-card__title">Send {{ channelLabel(selectedChannel) }}</div>
                <div class="modal-card__subtitle">To {{ selectedUser ? selectedUser.username : '' }}</div>
              </div>
            </div>
            <button class="modal-close" @click="closeModal"><i class="fas fa-times"></i></button>
          </div>

          <div class="modal-card__body modal-body--split">
            <!-- LEFT: Form -->
            <div class="modal-form-col">
              <div class="channel-picker">
                <button
                  v-for="ch in channels"
                  :key="ch.value"
                  class="channel-chip"
                  :class="{ 'channel-chip--active': selectedChannel === ch.value }"
                  @click="selectedChannel = ch.value"
                >
                  <i :class="ch.icon"></i>
                  <span>{{ ch.text }}</span>
                </button>
              </div>

              <button class="btn-use-template" @click="openTemplatePickerDialog('communication')">
                <i class="fas fa-layer-group"></i>
                <span>Use a template</span>
                <span v-if="templates.length" class="tpl-badge">{{ templates.length }}</span>
              </button>

              <transition name="field-slide">
                <div v-if="selectedChannel === 'email'" class="form-group">
                  <label class="form-label">Subject</label>
                  <input v-model="emailSubject" type="text" class="form-input" placeholder="Enter subject..." />
                </div>
              </transition>

              <div class="form-group">
                <label class="form-label">
                  Message
                  <span class="form-hint">{name} → user name · {link} → chat link</span>
                </label>
                <textarea
                  v-model="messageText"
                  class="form-textarea"
                  rows="5"
                  placeholder="Type your message..."
                  @input="updatePreview"
                ></textarea>
                <div class="form-actions-row">
                  <div class="char-count" :class="{ 'char-count--warn': messageText.length > 900 }">
                    {{ messageText.length }}/1000
                  </div>
                  <button v-if="messageText.trim()" class="btn-save-tpl" @click="openSaveTemplate('communication')">
                    <i class="fas fa-bookmark"></i>
                    Save as template
                  </button>
                </div>
              </div>
            </div>

            <!-- RIGHT: Preview -->
            <div class="modal-preview-col">
              <div class="preview-section preview-section--sticky">
                <div class="preview-header">
                  <i class="fas fa-eye"></i>
                  <span>Live Preview</span>
                  <span class="preview-channel">{{ channelLabel(selectedChannel) }}</span>
                </div>
                <div v-if="selectedChannel === 'whatsapp'" class="preview-whatsapp">
                  <div class="wa-phone">
                    <div class="wa-phone__header"><i class="fab fa-whatsapp"></i> WhatsApp</div>
                    <div class="wa-phone__chat">
                      <div class="wa-bubble">
                        <div class="wa-bubble__text">{{ previewMessage }}</div>
                        <div class="wa-bubble__meta">
                          <span class="wa-bubble__time">{{ currentTime }}</span>
                          <span class="wa-bubble__tick">✓✓</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div v-else-if="selectedChannel === 'email'" class="preview-email">
                  <div class="email-frame">
                    <div class="email-frame__bar">
                      <span class="email-frame__dots">● ● ●</span>
                      <span class="email-frame__url">pettycashglobal.com</span>
                    </div>
                    <div class="email-frame__body">
                      <div class="email-header">
                        <div class="email-header__from">
                          <div class="email-avatar">PC</div>
                          <div>
                            <div class="email-header__name">PettyCash Manager</div>
                            <div class="email-header__addr">manager@pettycashglobal.com</div>
                          </div>
                        </div>
                        <div class="email-header__date">{{ currentDate }}</div>
                      </div>
                      <div class="email-subject">{{ emailSubject || '(no subject)' }}</div>
                      <div class="email-body">{{ previewMessage }}</div>
                    </div>
                  </div>
                </div>
                <div v-else-if="selectedChannel === 'webpush'" class="preview-webpush">
                  <div class="push-frame">
                    <div class="push-frame__header">
                      <div class="push-app">
                        <div class="push-app__icon"><i class="fas fa-handshake"></i></div>
                        <div class="push-app__name">PettyCash</div>
                      </div>
                      <div class="push-time">now</div>
                    </div>
                    <div class="push-content">
                      <div class="push-title">Message from Manager</div>
                      <div class="push-body">{{ previewMessage }}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="modal-card__foot">
            <button class="btn btn--ghost" @click="closeModal">Cancel</button>
            <button
              class="btn btn--send"
              :class="'btn--send--' + selectedChannel"
              :disabled="!messageText.trim() || sending"
              @click="sendCommunication"
            >
              <i v-if="sending" class="fas fa-spinner fa-spin"></i>
              <i v-else :class="channelIcon(selectedChannel)"></i>
              {{ sending ? 'Sending…' : 'Send ' + channelLabel(selectedChannel) }}
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- ═══ Modal Chat In-App (avec templates) ══════════════════════ -->
    <transition name="modal">
      <div v-if="showChatModal" class="modal-overlay" @click.self="closeChatModal">
        <div class="modal-card modal-card--comm">
          <div class="modal-card__head">
            <div class="modal-card__brand">
              <span class="modal-card__icon modal-card__icon--chat">
                <i class="fas fa-comment-dots"></i>
              </span>
              <div>
                <div class="modal-card__title">Send In-App Message</div>
                <div class="modal-card__subtitle">To {{ chatUser ? chatUser.username : '' }}</div>
              </div>
            </div>
            <button class="modal-close" @click="closeChatModal"><i class="fas fa-times"></i></button>
          </div>

          <div class="modal-card__body modal-body--split">
            <div class="modal-form-col">
              <button class="btn-use-template" @click="openTemplatePickerDialog('chat')">
                <i class="fas fa-layer-group"></i>
                <span>Use a template</span>
                <span v-if="templates.length" class="tpl-badge">{{ templates.length }}</span>
              </button>

              <div class="form-group">
                <label class="form-label">
                  Message
                  <span class="form-hint">{name} → user name</span>
                </label>
                <textarea
                  v-model="chatMessageText"
                  class="form-textarea"
                  rows="5"
                  placeholder="Type your in-app message..."
                  @input="updateChatPreview"
                ></textarea>
                <div class="form-actions-row">
                  <div class="char-count" :class="{ 'char-count--warn': chatMessageText.length > 900 }">
                    {{ chatMessageText.length }}/1000
                  </div>
                  <button v-if="chatMessageText.trim()" class="btn-save-tpl" @click="openSaveTemplate('chat')">
                    <i class="fas fa-bookmark"></i>
                    Save as template
                  </button>
                </div>
              </div>
            </div>

            <div class="modal-preview-col">
              <div class="preview-section preview-section--sticky">
                <div class="preview-header">
                  <i class="fas fa-eye"></i>
                  <span>Live Preview</span>
                  <span class="preview-channel">In-App Chat</span>
                </div>
                <div class="preview-chat">
                  <div class="chat-frame">
                    <div class="chat-frame__header">
                      <div class="chat-frame__avatar"><i class="fas fa-headset"></i></div>
                      <div class="chat-frame__info">
                        <div class="chat-frame__name">Support</div>
                        <div class="chat-frame__status"><span class="chat-frame__dot"></span> Online</div>
                      </div>
                    </div>
                    <div class="chat-frame__body">
                      <div class="chat-bubble chat-bubble--out">
                        <div class="chat-bubble__text">{{ chatPreviewMessage }}</div>
                        <div class="chat-bubble__meta">
                          <span class="chat-bubble__time">{{ currentTime }}</span>
                          <i class="fas fa-check-double chat-bubble__tick"></i>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="modal-card__foot">
            <button class="btn btn--ghost" @click="closeChatModal">Cancel</button>
            <button
              class="btn btn--send btn--send--chat"
              :disabled="!chatMessageText.trim() || sendingChat"
              @click="confirmSendChat"
            >
              <i v-if="sendingChat" class="fas fa-spinner fa-spin"></i>
              <i v-else class="fas fa-paper-plane"></i>
              {{ sendingChat ? 'Sending…' : 'Send Message' }}
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- ═══ Template Picker Dialog ══════════════════════════ -->
    <transition name="modal">
      <div v-if="showTemplatePickerDialog" class="modal-overlay" @click.self="closeTemplatePickerDialog">
        <div class="modal-card modal-card--picker">
          <div class="modal-card__head">
            <div class="modal-card__brand">
              <span class="modal-card__icon modal-card__icon--tpl">
                <i class="fas fa-layer-group"></i>
              </span>
              <div>
                <div class="modal-card__title">Choose a Top‑up Template</div>
                <div class="modal-card__subtitle">{{ templates.length }} template{{ templates.length !== 1 ? 's' : '' }} available</div>
              </div>
            </div>
            <div class="modal-card__head-actions">
              <button class="btn btn--create-template-manager" @click="openCreateTemplateDialog">
                <i class="fas fa-plus"></i> New template
              </button>
              <button class="modal-close" @click="closeTemplatePickerDialog"><i class="fas fa-times"></i></button>
            </div>
          </div>
          <div class="modal-card__body">
            <div v-if="templatesLoading" class="tpl-loading tpl-loading--big">
              <i class="fas fa-spinner fa-spin"></i> Loading templates…
            </div>
            <div v-else-if="templates.length === 0" class="tpl-empty tpl-empty--big">
              <i class="fas fa-file-medical"></i>
              <p>No templates yet</p>
              <small>Create your first template to reuse messages</small>
            </div>
            <div v-else>
              <div class="tpl-search tpl-search--dialog">
                <i class="fas fa-search"></i>
                <input v-model="templatePickerSearch" type="text" placeholder="Search templates by name, subject or content..." />
                <button v-if="templatePickerSearch" class="search-clear" @click="templatePickerSearch = ''">
                  <i class="fas fa-times"></i>
                </button>
              </div>
              <div class="tpl-picker-list">
                <div v-for="tpl in filteredPickerTemplates" :key="tpl.id" class="tpl-picker-item" @click="selectTemplateFromPicker(tpl)">
                  <div class="tpl-picker-item__top">
                    <span class="tpl-item__name">{{ tpl.name }}</span>
                    <span class="tpl-all-channels-badge">
                      <i class="fas fa-share-alt"></i> All channels
                    </span>
                  </div>
                  <p v-if="tpl.subject" class="tpl-picker-item__subject">
                    <i class="fas fa-heading"></i> {{ tpl.subject }}
                  </p>
                  <p class="tpl-picker-item__body">{{ tpl.message }}</p>
                  <div class="tpl-picker-item__footer">
                    <span class="tpl-manager-item__date">
                      <i class="fas fa-clock"></i> {{ formatDate(tpl.created_at) }}
                    </span>
                    <div class="tpl-compat-icons">
                      <i class="fas fa-envelope" title="Email"></i>
                      <i class="fab fa-whatsapp" title="WhatsApp"></i>
                      <i class="fas fa-bell" title="WebPush"></i>
                      <i class="fas fa-comment-dots" title="Chat"></i>
                    </div>
                  </div>
                </div>
                <div v-if="filteredPickerTemplates.length === 0" class="tpl-empty tpl-empty--big">
                  <i class="fas fa-search"></i>
                  <p>No matching templates</p>
                  <small>Try a different search term</small>
                </div>
              </div>
            </div>
          </div>
          <div class="modal-card__foot">
            <button class="btn btn--ghost" @click="closeTemplatePickerDialog">Cancel</button>
          </div>
        </div>
      </div>
    </transition>

    <!-- ═══ Save Template Dialog ══════════════════════════ -->
    <transition name="modal">
      <div v-if="showSaveTemplateDialog" class="modal-overlay" @click.self="closeSaveTemplate">
        <div class="modal-card modal-card--save-tpl">
          <div class="modal-card__head">
            <div class="modal-card__brand">
              <span class="modal-card__icon modal-card__icon--tpl"><i class="fas fa-bookmark"></i></span>
              <div>
                <div class="modal-card__title">Save Template</div>
                <div class="modal-card__subtitle">Reuse this message later on any channel</div>
              </div>
            </div>
            <button class="modal-close" @click="closeSaveTemplate"><i class="fas fa-times"></i></button>
          </div>
          <div class="modal-card__body">
            <div class="form-group">
              <label class="form-label">Template name</label>
              <input v-model="templateName" type="text" class="form-input" placeholder="e.g. Welcome new investor" ref="tplNameInput" @keyup.enter="confirmSaveTemplate" />
            </div>
            <div class="form-group">
              <label class="form-label">Subject <span class="form-hint">Optional — auto-filled if email</span></label>
              <input v-model="saveTemplateSubject" type="text" class="form-input" placeholder="Leave blank if not applicable…" />
            </div>
            <div class="form-group">
              <label class="form-label">Message</label>
              <div class="tpl-msg-preview">{{ saveTemplateMessage }}</div>
            </div>
            <div class="tpl-info-note">
              <i class="fas fa-info-circle"></i>
              This template will be available for all channels (Email, WhatsApp, WebPush, Chat).
            </div>
          </div>
          <div class="modal-card__foot">
            <button class="btn btn--ghost" @click="closeSaveTemplate">Cancel</button>
            <button class="btn btn--save-confirm" :disabled="!templateName.trim() || savingTemplate" @click="confirmSaveTemplate">
              <i v-if="savingTemplate" class="fas fa-spinner fa-spin"></i>
              <i v-else class="fas fa-check"></i>
              {{ savingTemplate ? 'Saving…' : 'Save Template' }}
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- ═══ Template Manager Modal ════════════════════════ -->
    <transition name="modal">
      <div v-if="showTemplateManager" class="modal-overlay" @click.self="closeTemplateManager">
        <div class="modal-card modal-card--manager">
          <div class="modal-card__head">
            <div class="modal-card__brand">
              <span class="modal-card__icon modal-card__icon--tpl"><i class="fas fa-layer-group"></i></span>
              <div>
                <div class="modal-card__title">Top‑up Message Templates</div>
                <div class="modal-card__subtitle">{{ templates.length }} template{{ templates.length !== 1 ? 's' : '' }} saved</div>
              </div>
            </div>
            <div class="modal-card__head-actions">
              <button class="btn btn--create-template-manager" @click="openCreateTemplateDialog">
                <i class="fas fa-plus"></i> New template
              </button>
              <button class="modal-close" @click="closeTemplateManager"><i class="fas fa-times"></i></button>
            </div>
          </div>
          <div class="modal-card__body">
            <div v-if="templatesLoading" class="tpl-loading tpl-loading--big">
              <i class="fas fa-spinner fa-spin"></i> Loading templates…
            </div>
            <div v-else-if="templates.length === 0" class="tpl-empty tpl-empty--big">
              <i class="fas fa-file-medical"></i>
              <p>No templates yet</p>
            </div>
            <div v-else class="tpl-manager-list">
              <div v-for="tpl in templates" :key="tpl.id" class="tpl-manager-item">
                <div class="tpl-manager-item__top">
                  <span class="tpl-item__name">{{ tpl.name }}</span>
                  <div class="tpl-manager-item__actions">
                    <span class="tpl-all-channels-badge"><i class="fas fa-share-alt"></i> All channels</span>
                    <button class="tpl-del-btn" @click="requestDeleteTemplate(tpl)" title="Delete"><i class="fas fa-trash-alt"></i></button>
                  </div>
                </div>
                <p v-if="tpl.subject" class="tpl-manager-item__subject"><i class="fas fa-heading"></i> {{ tpl.subject }}</p>
                <p class="tpl-manager-item__body">{{ tpl.message }}</p>
                <div class="tpl-manager-item__footer">
                  <span class="tpl-manager-item__date"><i class="fas fa-clock"></i> {{ formatDate(tpl.created_at) }}</span>
                  <div class="tpl-compat-icons">
                    <i class="fas fa-envelope" title="Email"></i>
                    <i class="fab fa-whatsapp" title="WhatsApp"></i>
                    <i class="fas fa-bell" title="WebPush"></i>
                    <i class="fas fa-comment-dots" title="Chat"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="modal-card__foot">
            <button class="btn btn--ghost" @click="closeTemplateManager">Close</button>
          </div>
        </div>
      </div>
    </transition>

    <!-- ═══ Create Template Dialog ════════════ -->
    <transition name="modal">
      <div v-if="showCreateTemplateDialog" class="modal-overlay" @click.self="closeCreateTemplateDialog">
        <div class="modal-card modal-card--save-tpl">
          <div class="modal-card__head">
            <div class="modal-card__brand">
              <span class="modal-card__icon modal-card__icon--tpl"><i class="fas fa-plus-circle"></i></span>
              <div>
                <div class="modal-card__title">Create new template</div>
                <div class="modal-card__subtitle">Save a reusable message for any channel</div>
              </div>
            </div>
            <button class="modal-close" @click="closeCreateTemplateDialog"><i class="fas fa-times"></i></button>
          </div>
          <div class="modal-card__body">
            <div class="form-group">
              <label class="form-label">Template name</label>
              <input v-model="createTemplateName" type="text" class="form-input" placeholder="e.g. Welcome new investor" ref="createTplNameInput" @keyup.enter="confirmCreateTemplate" />
            </div>
            <div class="form-group">
              <label class="form-label">Subject <span class="form-hint">Optional — mainly for email</span></label>
              <input v-model="createTemplateSubject" type="text" class="form-input" placeholder="Leave empty if not applicable…" />
            </div>
            <div class="form-group">
              <label class="form-label">Message <span class="form-hint">{name} → user name · {link} → chat link</span></label>
              <textarea v-model="createTemplateMessage" class="form-textarea" rows="5" placeholder="Type your template message…"></textarea>
              <div class="form-actions-row">
                <div class="char-count" :class="{ 'char-count--warn': createTemplateMessage.length > 900 }">{{ createTemplateMessage.length }}/1000</div>
              </div>
            </div>
            <div class="tpl-info-note">
              <i class="fas fa-info-circle"></i>
              This template will be available for all channels (Email, WhatsApp, WebPush, Chat).
            </div>
          </div>
          <div class="modal-card__foot">
            <button class="btn btn--ghost" @click="closeCreateTemplateDialog">Cancel</button>
            <button class="btn btn--save-confirm" :disabled="!createTemplateName.trim() || savingTemplate" @click="confirmCreateTemplate">
              <i v-if="savingTemplate" class="fas fa-spinner fa-spin"></i>
              <i v-else class="fas fa-check"></i>
              {{ savingTemplate ? 'Saving…' : 'Create template' }}
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- ═══ Delete Confirmation Mini Dialog ════════════ -->
    <transition name="modal">
      <div v-if="showDeleteConfirmation" class="modal-overlay" @click.self="cancelDeleteTemplate">
        <div class="modal-card modal-card--confirm">
          <div class="modal-card__head">
            <div class="modal-card__brand">
              <span class="modal-card__icon modal-card__icon--delete">
                <i class="fas fa-exclamation-triangle"></i>
              </span>
              <div>
                <div class="modal-card__title">Delete Template</div>
                <div class="modal-card__subtitle">This action cannot be undone</div>
              </div>
            </div>
            <button class="modal-close" @click="cancelDeleteTemplate"><i class="fas fa-times"></i></button>
          </div>
          <div class="modal-card__body">
            <p class="confirm-text">
              Are you sure you want to delete the template<br>
              <strong>"{{ templateToDelete ? templateToDelete.name : '' }}"</strong>?
            </p>
          </div>
          <div class="modal-card__foot">
            <button class="btn btn--ghost" @click="cancelDeleteTemplate">Cancel</button>
            <button class="btn btn--delete-confirm" @click="confirmDeleteTemplate">
              <i class="fas fa-trash-alt"></i> Delete
            </button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import axios from 'axios';

const CHAT_LINK = 'https://myapp.pettycashglobal.com/?open_chat=true';

export default {
  name: 'TopUpList',

  filters: {
    truncate(val, len) {
      if (!val) return '';
      return val.length > len ? val.slice(0, len) + '…' : val;
    },
  },

  data() {
    const saved = (() => {
      try { return JSON.parse(sessionStorage.getItem('topups_state') || 'null'); }
      catch { return null; }
    })();

    return {
      requestedTopUps:    saved?.results        || [],
      selectedZoneFilter: saved?.zone           || null,
      selectedStartDate:  saved?.startDate      || null,
      selectedEndDate:    saved?.endDate        || null,

      stateRestoredFromCache: !!saved?.results?.length,
      zone_types: [],

      search: '',
      loading: false,
      isMobile: false,

      statusColor: {
        Active: 'primary',
        Validated: 'success',
        Inactive: 'error',
        Pending: 'warning',
      },

      headers: [
        { text: 'Name',      value: 'username',                   width: '170px' },
        { text: 'Principal', value: 'amount',                     width: '130px', align: 'end' },
        { text: 'Loan',      value: 'amount_deposited_loan',      width: '110px', align: 'end' },
        { text: 'Campaign',  value: 'amount_deposited_campaign',  width: '120px', align: 'end' },
        { text: 'Withdrawn', value: 'amount_withdrawn_campaign',  width: '110px', align: 'end' },
        { text: 'Status',    value: 'status',                     width: '100px', align: 'center' },
        { text: 'Joined',    value: 'date_time',                  width: '110px', align: 'center' },
        { text: '',          value: 'actions', sortable: false,   width: '190px', align: 'center' },
      ],

      // ── Modal communication ──
      showModal: false,
      selectedChannel: 'email',
      selectedUser: null,
      channels: [
        { text: 'Email',    value: 'email',    icon: 'fas fa-envelope' },
        { text: 'WhatsApp', value: 'whatsapp', icon: 'fab fa-whatsapp' },
        { text: 'WebPush',  value: 'webpush',  icon: 'fas fa-bell' },
      ],
      emailSubject: 'Message from PettyCash',
      messageText: '',
      sending: false,
      previewMessage: '',

      // ── Modal chat ──
      showChatModal: false,
      chatUser: null,
      chatMessageText: '',
      chatPreviewMessage: '',
      sendingChat: false,

      // ── Templates ──
      templates: [],
      templatesLoading: false,

      // Template picker dialog
      showTemplatePickerDialog: false,
      templatePickerSource: 'communication', // 'communication' ou 'chat'
      templatePickerSearch: '',

      // Save‑template dialog
      showSaveTemplateDialog: false,
      templateName: '',
      saveTemplateSubject: '',
      saveTemplateMessage: '',
      savingTemplate: false,
      saveTemplateSource: 'communication',

      // Template manager
      showTemplateManager: false,

      // Create template dialog
      showCreateTemplateDialog: false,
      createTemplateName: '',
      createTemplateSubject: '',
      createTemplateMessage: '',

      // Suppression mini dialogue
      showDeleteConfirmation: false,
      templateToDelete: null,
    };
  },

  computed: {
    filteredTopUps() {
      let list = this.requestedTopUps;
      if (this.search.trim()) {
        const q = this.search.toLowerCase();
        list = list.filter((u) => u.username.toLowerCase().includes(q));
      }
      return list;
    },

    filteredPickerTemplates() {
      if (!this.templatePickerSearch.trim()) return this.templates;
      const q = this.templatePickerSearch.toLowerCase().trim();
      return this.templates.filter(t =>
        t.name.toLowerCase().includes(q) ||
        (t.message && t.message.toLowerCase().includes(q)) ||
        (t.subject && t.subject.toLowerCase().includes(q))
      );
    },

    statsCards() {
      const total     = this.requestedTopUps.length || 1;
      const active    = this.requestedTopUps.filter((u) => u.status === 'Active').length;
      const inactive  = this.requestedTopUps.filter((u) => u.status === 'Inactive').length;
      const validated = this.requestedTopUps.filter((u) => u.status === 'Validated').length;
      return [
        { key: 'total',     label: 'Total Top‑ups', value: this.requestedTopUps.length, icon: 'fas fa-credit-card',   color: '#5c6bc0', bg: '#eef0fc', percent: 100 },
        { key: 'active',    label: 'Active',         value: active,                      icon: 'fas fa-check-circle',  color: '#2e7d32', bg: '#e8f5e9', percent: (active    / total) * 100 },
        { key: 'inactive',  label: 'Inactive',       value: inactive,                    icon: 'fas fa-times-circle',  color: '#c62828', bg: '#ffebee', percent: (inactive  / total) * 100 },
        { key: 'validated', label: 'Validated',      value: validated,                   icon: 'fas fa-check-double',  color: '#00695c', bg: '#e0f2f1', percent: (validated / total) * 100 },
      ];
    },

    currentTime() {
      return new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    },
    currentDate() {
      return new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    },
  },

  mounted() {
    this.checkMobile();
    window.addEventListener('resize', this.checkMobile);
    document.addEventListener('keydown', this.handleEscape);

    this.getZones();
    this.fetchTemplates();

    if (this.stateRestoredFromCache) {
      this.$nextTick(() => this.fetchTopUps({ silent: true }));
    }
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.checkMobile);
    document.removeEventListener('keydown', this.handleEscape);
  },

  methods: {
    // ── Helpers ──
    checkMobile() {
      this.isMobile = window.innerWidth < 768;
    },
    formatDate(date) {
      if (!date) return 'N/A';
      return new Date(date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    },
    numberFormat(value) {
      if (!value) return '0';
      return Number(value).toLocaleString('fr-FR');
    },

    // ── Top‑ups ──
    async getZones() {
      try {
        const res = await axios.get('/api/v1/manager/get/topup/zones/');
        this.zone_types = res.data;
        if (!this.selectedZoneFilter && res.data.length > 0) {
          this.selectedZoneFilter = res.data[0].id;
          this.fetchTopUps();
        }
      } catch {
        this.showSnackbar('Error loading zones', 'error');
      }
    },
    async fetchTopUps({ silent = false } = {}) {
      if (!this.selectedZoneFilter) {
        this.showSnackbar('Please select a zone', 'error');
        return;
      }
      if (!silent) this.loading = true;
      const start = this.selectedStartDate || 'null';
      const end   = this.selectedEndDate   || 'null';
      try {
        const res = await axios.get(`/api/v1/manager/get/topups/${this.selectedZoneFilter}/${start}/${end}/`);
        this.requestedTopUps = res.data;
        try {
          sessionStorage.setItem('topups_state', JSON.stringify({
            zone:      this.selectedZoneFilter,
            startDate: this.selectedStartDate,
            endDate:   this.selectedEndDate,
            results:   res.data,
          }));
        } catch { /* ignore */ }
      } catch {
        this.showSnackbar('Error loading top‑ups', 'error');
      } finally {
        this.loading = false;
      }
    },
    goCreate() {
      this.$router.push({ name: 'savings.topup_create' });
    },
    goRecharge() {
      this.$router.push({ name: 'savings.topup_recharge' });
    },
    viewDetails(id) {
      this.$router.push('/savings/topup/detail/' + id).catch(() => {});
    },

    // ── Communication (email/whatsapp/webpush) ──
    openCommunication(user, channel) {
      this.selectedUser    = user;
      this.selectedChannel = channel;
      this.messageText     = '';
      this.emailSubject    = 'Message from PettyCash';
      this.previewMessage  = this.renderPreview('', user);
      this.showModal       = true;
    },
    closeModal() {
      this.showModal    = false;
      this.messageText  = '';
      this.selectedUser = null;
    },
    renderPreview(text, user) {
      if (!text) return 'Your message will appear here…';
      let rendered = text;
      rendered = rendered.replace(/{name}/g, user ? user.username : 'John Doe');
      rendered = rendered.replace(/{link}/g, CHAT_LINK);
      return rendered;
    },
    updatePreview() {
      this.previewMessage = this.renderPreview(this.messageText, this.selectedUser);
    },
    channelIcon(channel) {
      const map = { email: 'fas fa-envelope', whatsapp: 'fab fa-whatsapp', webpush: 'fas fa-bell' };
      return map[channel] || 'fas fa-paper-plane';
    },
    channelLabel(channel) {
      const map = { email: 'Email', whatsapp: 'WhatsApp', webpush: 'WebPush' };
      return map[channel] || channel;
    },
    async sendCommunication() {
      if (!this.messageText.trim()) {
        this.showSnackbar('Please enter a message', 'error');
        return;
      }
      this.sending = true;
      try {
        const user = this.selectedUser;
        let endpoint = '';
        let payload  = {};
        if (this.selectedChannel === 'email') {
          endpoint = `/api/v1/manager/partners/${user.id}/send-email/`;
          payload  = { subject: this.emailSubject, message: this.messageText };
        } else if (this.selectedChannel === 'whatsapp') {
          endpoint = `/api/v1/manager/partners/${user.id}/send-whatsapp/`;
          payload  = { message: this.messageText };
        } else {
          endpoint = `/api/v1/manager/partners/${user.id}/send-webpush/`;
          payload  = { title: 'Message from Manager', message: this.messageText };
        }
        const response = await axios.post(endpoint, payload);
        if (this.selectedChannel === 'whatsapp') {
          if (response.data.whatsapp_url) {
            window.open(response.data.whatsapp_url, '_blank');
            this.closeModal();
          } else {
            this.showSnackbar('WhatsApp link unavailable', 'error');
          }
        } else {
          this.showSnackbar(response.data.message || 'Message sent', 'success');
          this.closeModal();
        }
      } catch (error) {
        const msg = error.response?.data?.error || 'Error sending message';
        this.showSnackbar(msg, 'error');
      } finally {
        this.sending = false;
      }
    },

    // ── Chat in‑app ──
    sendInAppMessage(user) {
      if (!user) return;
      this.chatUser = user;
      this.chatMessageText = `Hello ${user.username}, we have an important update for you. Please check your dashboard.`;
      this.chatPreviewMessage = this.renderChatPreview(this.chatMessageText, user);
      this.showChatModal = true;
    },
    closeChatModal() {
      this.showChatModal    = false;
      this.chatMessageText  = '';
      this.chatUser         = null;
    },
    renderChatPreview(text, user) {
      if (!text) return 'Your message will appear here…';
      let rendered = text;
      rendered = rendered.replace(/{name}/g, user ? user.username : 'John Doe');
      return rendered;
    },
    updateChatPreview() {
      this.chatPreviewMessage = this.renderChatPreview(this.chatMessageText, this.chatUser);
    },
    async confirmSendChat() {
      if (!this.chatMessageText.trim()) {
        this.showSnackbar('Please enter a message', 'error');
        return;
      }
      this.sendingChat = true;
      try {
        const formData = new FormData();
        formData.append('id_client',    this.chatUser.id);
        formData.append('message',      this.chatMessageText);
        formData.append('message_type', 'text');
        await axios.post('/api/v1/manager/save_chat/', formData);
        this.showSnackbar(`Message sent to ${this.chatUser.username}`, 'success');
        this.closeChatModal();
      } catch (error) {
        console.error('Chat error:', error);
        this.showSnackbar('Error sending message', 'error');
      } finally {
        this.sendingChat = false;
      }
    },

    // ── Templates avec catégorie "topup" ──
    async fetchTemplates() {
      this.templatesLoading = true;
      try {
        // On ne charge que les templates de la catégorie "topup"
        const response = await axios.get('/api/v1/manager/message-templates/?category=topup');
        this.templates = Array.isArray(response.data) ? response.data : (response.data.results || response.data.data || []);
      } catch (error) {
        console.error('Error loading templates:', error);
        this.templates = [];
      } finally {
        this.templatesLoading = false;
      }
    },

    // Template Picker Dialog
    openTemplatePickerDialog(source) {
      this.templatePickerSource = source;
      this.templatePickerSearch = '';
      this.showTemplatePickerDialog = true;
    },
    closeTemplatePickerDialog() {
      this.showTemplatePickerDialog = false;
      this.templatePickerSearch = '';
    },
    selectTemplateFromPicker(tpl) {
      if (this.templatePickerSource === 'communication') {
        if (tpl.channel && this.channels.some(c => c.value === tpl.channel)) {
          this.selectedChannel = tpl.channel;
        }
        this.messageText = tpl.message;
        if (tpl.subject) this.emailSubject = tpl.subject;
        this.$nextTick(() => this.updatePreview());
      } else { // chat
        this.chatMessageText = tpl.message;
        this.$nextTick(() => this.updateChatPreview());
      }
      this.closeTemplatePickerDialog();
    },

    // Save template
    openSaveTemplate(source) {
      this.saveTemplateSource = source;
      if (source === 'communication') {
        this.saveTemplateMessage = this.messageText;
        this.saveTemplateSubject = this.selectedChannel === 'email' ? this.emailSubject : '';
      } else {
        this.saveTemplateMessage = this.chatMessageText;
        this.saveTemplateSubject = '';
      }
      this.templateName = '';
      this.showSaveTemplateDialog = true;
      this.$nextTick(() => { if (this.$refs.tplNameInput) this.$refs.tplNameInput.focus(); });
    },
    closeSaveTemplate() {
      this.showSaveTemplateDialog = false;
      this.templateName = '';
    },
    async confirmSaveTemplate() {
      if (!this.templateName.trim() || this.savingTemplate) return;
      this.savingTemplate = true;
      try {
        const payload = {
          name: this.templateName.trim(),
          subject: this.saveTemplateSubject.trim(),
          message: this.saveTemplateMessage,
          category: 'topup',   // obligatoire
        };
        const response = await axios.post('/api/v1/manager/message-templates/', payload);
        this.templates.unshift(response.data);
        this.showSnackbar('Template saved!', 'success');
        this.closeSaveTemplate();
      } catch (error) {
        this.showSnackbar(error.response?.data?.error || 'Error saving template', 'red darken-4');
      } finally {
        this.savingTemplate = false;
      }
    },

    // Suppression avec mini dialogue
    requestDeleteTemplate(tpl) {
      this.templateToDelete = tpl;
      this.showDeleteConfirmation = true;
    },
    cancelDeleteTemplate() {
      this.showDeleteConfirmation = false;
      this.templateToDelete = null;
    },
    async confirmDeleteTemplate() {
      if (!this.templateToDelete) return;
      const id = this.templateToDelete.id;
      try {
        await axios.delete(`/api/v1/manager/message-templates/${id}/`);
        this.templates = this.templates.filter(t => t.id !== id);
        this.showSnackbar('Template deleted', 'success');
      } catch (error) {
        this.showSnackbar('Error deleting template', 'red darken-4');
      } finally {
        this.cancelDeleteTemplate();
      }
    },

    openTemplateManager() { this.showTemplateManager = true; },
    closeTemplateManager() { this.showTemplateManager = false; },

    openCreateTemplateDialog() {
      this.createTemplateName = '';
      this.createTemplateSubject = 'Message from PettyCash';
      this.createTemplateMessage = '';
      this.showCreateTemplateDialog = true;
      this.$nextTick(() => { if (this.$refs.createTplNameInput) this.$refs.createTplNameInput.focus(); });
    },
    closeCreateTemplateDialog() {
      this.showCreateTemplateDialog = false;
    },
    async confirmCreateTemplate() {
      if (!this.createTemplateName.trim() || this.savingTemplate) return;
      this.savingTemplate = true;
      try {
        const payload = {
          name: this.createTemplateName.trim(),
          subject: this.createTemplateSubject.trim(),
          message: this.createTemplateMessage,
          category: 'topup',   // obligatoire
        };
        const response = await axios.post('/api/v1/manager/message-templates/', payload);
        this.templates.unshift(response.data);
        this.showSnackbar('Template created!', 'success');
        this.closeCreateTemplateDialog();
      } catch (error) {
        this.showSnackbar(error.response?.data?.error || 'Error creating template', 'red darken-4');
      } finally {
        this.savingTemplate = false;
      }
    },

    // ── Misc ──
    handleEscape(e) {
      if (e.key === 'Escape') {
        if (this.showDeleteConfirmation)  { this.cancelDeleteTemplate(); return; }
        if (this.showTemplatePickerDialog) { this.closeTemplatePickerDialog(); return; }
        if (this.showModal)               { this.closeModal(); return; }
        if (this.showChatModal)           { this.closeChatModal(); return; }
        if (this.showSaveTemplateDialog)  { this.closeSaveTemplate(); return; }
        if (this.showTemplateManager)     { this.closeTemplateManager(); return; }
        if (this.showCreateTemplateDialog) { this.closeCreateTemplateDialog(); return; }
      }
    },
    showSnackbar(message, type) {
      this.$store.commit('setSnackBarMessage', message);
      this.$store.commit('setSnackBarColor', type === 'error' ? 'red darken-4' : 'success');
      this.$store.commit('activateSnackBar', true);
    },
  },
};
</script>



<style scoped>
/* ═══════════════════════════════════════════════════════
   ROOT
   ═══════════════════════════════════════════════════════ */
.dash-root {
  padding: 20px;
  background: #f4f6fb;
  min-height: 100vh;
  font-family: 'Inter', system-ui, -apple-system, sans-serif;
  color: #2d3142;
}

/* ═══════════════════════════════════════════════════════
   HERO
   ═══════════════════════════════════════════════════════ */
.dash-hero {
  position: relative;
  background: #ffffff;
  border-radius: 20px;
  box-shadow: 0 4px 20px rgba(44, 51, 73, 0.06);
  margin-bottom: 20px;
  overflow: hidden;
}
.dash-hero__bg {
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, #5c6bc0 0%, #7986cb 100%);
  opacity: 0.04;
  pointer-events: none;
}
.dash-hero__inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 22px 28px 16px;
  flex-wrap: wrap;
  gap: 14px;
  position: relative;
  z-index: 1;
}
.dash-hero__brand {
  display: flex;
  align-items: center;
  gap: 14px;
}
.dash-hero__icon {
  width: 48px;
  height: 48px;
  background: #eef0fc;
  color: #5c6bc0;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  box-shadow: 0 2px 8px rgba(92, 107, 192, 0.15);
  flex-shrink: 0;
}
.dash-hero__title {
  margin: 0;
  font-size: 22px;
  font-weight: 800;
  letter-spacing: -0.4px;
  color: #1a1a2e;
  line-height: 1.2;
}
.dash-hero__subtitle {
  margin: 3px 0 0;
  font-size: 13px;
  color: #7a82a6;
  font-weight: 500;
}
.dash-hero__actions {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

/* ── Template Manager button ── */
.btn--templates-manager {
  background: #f3e5f5;
  color: #6a1b9a;
  border: 1px solid #e1bee7;
}
.btn--templates-manager:hover {
  background: #e1bee7;
}

/* Zone bar */
.zone-bar {
  position: relative;
  z-index: 1;
  border-top: 1px solid #eef0fc;
  background: rgba(255, 255, 255, 0.6);
  backdrop-filter: blur(8px);
}
.zone-bar__inner {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 14px 28px;
  flex-wrap: wrap;
}
.zone-picker {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1 1 240px;
  max-width: 320px;
}
.zone-picker__icon {
  color: #5c6bc0;
  font-size: 15px;
  width: 32px;
  height: 32px;
  background: #eef0fc;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.zone-picker__select {
  min-width: 180px;
  flex: 1;
}
.zone-divider {
  width: 1px;
  height: 32px;
  background: #e9ecf5;
  flex-shrink: 0;
}
.date-picker {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1 1 300px;
  max-width: 400px;
}
.date-picker__icon {
  color: #5c6bc0;
  font-size: 15px;
  width: 32px;
  height: 32px;
  background: #eef0fc;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.date-input {
  padding: 8px 12px;
  border: 1.5px solid #e9ecf5;
  border-radius: 10px;
  font-size: 13px;
  background: #f9fafc;
  transition: border-color 0.2s, background 0.2s, box-shadow 0.2s;
  outline: none;
  color: #2d3142;
  font-family: inherit;
  flex: 1;
  min-width: 0;
}
.date-input:focus {
  border-color: #5c6bc0;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(92, 107, 192, 0.08);
}
.date-input::-webkit-calendar-picker-indicator {
  opacity: 0.5;
  cursor: pointer;
}
.date-sep {
  color: #9aa0ba;
  font-size: 13px;
  font-weight: 600;
  flex-shrink: 0;
}

/* Buttons */
.btn {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 9px 18px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: all 0.18s cubic-bezier(0.4, 0, 0.2, 1);
  white-space: nowrap;
  outline: none;
  flex-shrink: 0;
}
.btn__text { display: inline; }
.btn--primary {
  background: #5c6bc0;
  color: #fff;
  box-shadow: 0 2px 10px rgba(92, 107, 192, 0.25);
}
.btn--primary:hover {
  background: #4a5ab5;
  transform: translateY(-1px);
  box-shadow: 0 4px 14px rgba(92, 107, 192, 0.3);
}
.btn--soft {
  background: #eef0fc;
  color: #5c6bc0;
}
.btn--soft:hover {
  background: #dde1f8;
  transform: translateY(-1px);
}
.btn--search {
  background: #5c6bc0;
  color: #fff;
  padding: 9px 20px;
  border-radius: 10px;
  gap: 8px;
}
.btn--search:hover:not(:disabled) {
  background: #4a5ab5;
  transform: translateY(-1px);
}
.btn--search:disabled {
  opacity: 0.45;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

/* ═══════════════════════════════════════════════════════
   STATS
   ═══════════════════════════════════════════════════════ */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 20px;
}
.stat-card {
  background: #ffffff;
  border-radius: 16px;
  padding: 18px 20px;
  display: flex;
  align-items: center;
  gap: 14px;
  position: relative;
  overflow: hidden;
  border: 1px solid #f0f2f8;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  cursor: default;
}
.stat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(44, 51, 73, 0.07);
}
.stat-card__icon {
  width: 42px;
  height: 42px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  flex-shrink: 0;
  transition: transform 0.3s ease;
}
.stat-card:hover .stat-card__icon { transform: scale(1.08); }
.stat-card__body { flex: 1; min-width: 0; }
.stat-card__val {
  display: block;
  font-size: 22px;
  font-weight: 800;
  color: #1a1a2e;
  line-height: 1.1;
  letter-spacing: -0.5px;
}
.stat-card__lbl {
  display: block;
  font-size: 12px;
  color: #7a82a6;
  font-weight: 500;
  margin-top: 3px;
}
.stat-card__bar {
  position: absolute;
  bottom: 0;
  left: 0;
  height: 3px;
  border-radius: 0 0 0 3px;
  transition: width 0.8s cubic-bezier(0.4, 0, 0.2, 1);
}

/* ═══════════════════════════════════════════════════════
   TABLE
   ═══════════════════════════════════════════════════════ */
.table-wrap {
  background: #ffffff;
  border-radius: 20px;
  box-shadow: 0 4px 20px rgba(44, 51, 73, 0.06);
  overflow: hidden;
}
.table-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  border-bottom: 1px solid #eef0fc;
  flex-wrap: wrap;
  gap: 12px;
}
.search-group {
  display: flex;
  align-items: center;
  gap: 10px;
  background: #f9fafc;
  border: 1.5px solid #e9ecf5;
  border-radius: 12px;
  padding: 0 14px;
  transition: border-color 0.2s, background 0.2s, box-shadow 0.2s;
  flex: 1;
  max-width: 300px;
}
.search-group:focus-within {
  border-color: #5c6bc0;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(92, 107, 192, 0.08);
}
.search-group__icon { color: #9aa0ba; font-size: 13px; flex-shrink: 0; }
.search-input {
  flex: 1;
  border: none;
  outline: none;
  padding: 10px 0;
  background: transparent;
  font-size: 13px;
  color: #2d3142;
  font-family: inherit;
  min-width: 0;
}
.search-input::placeholder { color: #b0b6cc; }
.search-clear {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border: none;
  background: transparent;
  color: #9aa0ba;
  cursor: pointer;
  font-size: 11px;
  border-radius: 50%;
  transition: all 0.15s ease;
  flex-shrink: 0;
}
.search-clear:hover { background: #eef0fc; color: #5c6bc0; }
.table-meta {
  font-size: 13px;
  color: #6b7a99;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 7px;
  flex-shrink: 0;
}
.table-meta i { color: #5c6bc0; font-size: 14px; }

/* Desktop table */
.table-desktop { overflow-x: auto; }
.table-desktop::-webkit-scrollbar { height: 6px; }
.table-desktop::-webkit-scrollbar-track { background: transparent; }
.table-desktop::-webkit-scrollbar-thumb { background: #d1d5e4; border-radius: 3px; }

.user-cell { display: flex; align-items: center; gap: 10px; }
.user-avatar {
  width: 34px; height: 34px;
  border-radius: 10px;
  background: #eef0fc; color: #5c6bc0;
  display: flex; align-items: center; justify-content: center;
  font-size: 13px; font-weight: 700; flex-shrink: 0;
}
.user-info { display: flex; flex-direction: column; min-width: 0; }
.user-name {
  font-weight: 700; font-size: 13px; color: #1a1a2e;
  line-height: 1.3; white-space: nowrap; overflow: hidden;
  text-overflow: ellipsis; max-width: 120px;
}
.user-zone { font-size: 11px; color: #9aa0ba; font-weight: 500; line-height: 1.2; }
.amount-cell  { font-weight: 700; font-size: 13px; color: #1a1a2e; font-variant-numeric: tabular-nums; white-space: nowrap; }
.deposit-cell { font-weight: 600; font-size: 13px; color: #2e7d32; font-variant-numeric: tabular-nums; white-space: nowrap; }
.withdraw-cell{ font-weight: 600; font-size: 13px; color: #c62828; font-variant-numeric: tabular-nums; white-space: nowrap; }
.status-chip  { font-weight: 700 !important; font-size: 11px !important; letter-spacing: 0.3px; }
.date-cell    { font-size: 12px; color: #6b7a99; font-weight: 500; white-space: nowrap; }

/* Vuetify overrides */
.topups-table ::v-deep .v-data-table-header { background: #f9fafc !important; }
.topups-table ::v-deep th {
  font-size: 11px !important; font-weight: 700 !important; color: #6b7a99 !important;
  text-transform: uppercase; letter-spacing: 0.6px;
  border-bottom: 1px solid #eef0fc !important; padding: 10px 12px !important; white-space: nowrap;
}
.topups-table ::v-deep td {
  font-size: 13px !important; color: #2d3142 !important;
  border-bottom: 1px solid #f4f6fb !important; padding: 10px 12px !important; vertical-align: middle !important;
}
.topups-table ::v-deep tr:hover { background: #f8f9fc !important; }
.topups-table ::v-deep .v-data-footer {
  border-top: 1px solid #eef0fc !important; font-size: 12px; color: #6b7a99; padding: 8px 16px;
}

/* ── Action buttons (desktop) ── */
.action-group { display: flex; gap: 4px; justify-content: center; }
.action-btn {
  width: 30px; height: 30px;
  border-radius: 8px; border: none;
  display: flex; align-items: center; justify-content: center;
  cursor: pointer; font-size: 12px;
  transition: all 0.18s cubic-bezier(0.4, 0, 0.2, 1);
  background: transparent; color: #9aa0ba;
}
.action-btn:hover { transform: translateY(-2px) scale(1.05); }
.action-btn--view:hover     { background: #eef0fc; color: #5c6bc0;  box-shadow: 0 2px 8px rgba(92,107,192,0.15); }
.action-btn--whatsapp:hover { background: #e9fbf0; color: #25d366;  box-shadow: 0 2px 8px rgba(37,211,102,0.15); }
.action-btn--email:hover    { background: #fce4ec; color: #ea4335;  box-shadow: 0 2px 8px rgba(234,67,53,0.12);  }
.action-btn--webpush:hover  { background: #fff3e0; color: #f4a84a;  box-shadow: 0 2px 8px rgba(244,168,74,0.15); }
.action-btn--chat:hover     { background: #eef0fc; color: #5c6bc0;  box-shadow: 0 2px 8px rgba(92,107,192,0.15); }

/* Empty */
.empty-table { text-align: center; padding: 56px 20px; color: #9aa0ba; }
.empty-table__illustration {
  width: 72px; height: 72px; background: #f4f6fb; border-radius: 20px;
  display: flex; align-items: center; justify-content: center; margin: 0 auto 16px;
}
.empty-table__illustration i { font-size: 28px; color: #b0b6cc; }
.empty-table p   { font-size: 16px; font-weight: 700; color: #6b7a99; margin: 0 0 6px; }
.empty-table small { font-size: 13px; color: #9aa0ba; }

/* ═══════════════════════════════════════════════════════
   MOBILE CARDS
   ═══════════════════════════════════════════════════════ */
.mobile-cards { padding: 16px; }
.mobile-cards__grid { display: flex; flex-direction: column; gap: 14px; }
.mobile-card {
  background: #ffffff; border-radius: 16px;
  border: 1px solid #f0f2f8;
  box-shadow: 0 2px 12px rgba(44, 51, 73, 0.04);
  overflow: hidden;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}
.mobile-card:hover { transform: translateY(-2px); box-shadow: 0 6px 20px rgba(44,51,73,0.08); }
.mobile-card__header {
  display: flex; align-items: center; gap: 12px;
  padding: 16px 16px 12px; border-bottom: 1px solid #f4f6fb;
}
.mobile-card__avatar {
  width: 42px; height: 42px; border-radius: 12px;
  background: #eef0fc; color: #5c6bc0;
  display: flex; align-items: center; justify-content: center;
  font-size: 16px; font-weight: 800; flex-shrink: 0;
}
.mobile-card__info { flex: 1; min-width: 0; }
.mobile-card__name {
  font-size: 15px; font-weight: 800; color: #1a1a2e; line-height: 1.3;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.mobile-card__zone { font-size: 12px; color: #9aa0ba; font-weight: 500; margin-top: 2px; display: flex; align-items: center; gap: 4px; }
.mobile-card__zone i { font-size: 10px; }
.mobile-card__body { padding: 12px 16px; display: flex; flex-direction: column; gap: 8px; }
.mobile-card__row { display: flex; justify-content: space-between; align-items: center; gap: 12px; }
.mobile-card__label { font-size: 12px; color: #9aa0ba; font-weight: 600; flex-shrink: 0; }
.mobile-card__value { font-size: 13px; font-weight: 700; color: #2d3142; font-variant-numeric: tabular-nums; text-align: right; }
.mobile-card__value--primary { color: #1a1a2e; font-size: 14px; }
.mobile-card__value--danger  { color: #c62828; }

/* ── Mobile action grid → 5 colonnes ── */
.mobile-card__actions {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 6px;
  padding: 12px 16px 16px;
  border-top: 1px solid #f4f6fb;
}
.mobile-action {
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  gap: 4px; padding: 10px 4px;
  border-radius: 10px; border: none; cursor: pointer;
  font-size: 10px; font-weight: 700;
  transition: all 0.15s ease;
  background: #f9fafc; color: #6b7a99;
}
.mobile-action i { font-size: 15px; }
.mobile-action--view:hover     { background: #eef0fc; color: #5c6bc0;  transform: translateY(-1px); }
.mobile-action--whatsapp:hover { background: #e9fbf0; color: #25d366;  transform: translateY(-1px); }
.mobile-action--email:hover    { background: #fce4ec; color: #ea4335;  transform: translateY(-1px); }
.mobile-action--webpush:hover  { background: #fff3e0; color: #f4a84a;  transform: translateY(-1px); }
.mobile-action--chat:hover     { background: #eef0fc; color: #5c6bc0;  transform: translateY(-1px); }

/* Card list transitions */
.card-list-enter-active, .card-list-leave-active { transition: all 0.3s cubic-bezier(0.4,0,0.2,1); }
.card-list-enter, .card-list-leave-to           { opacity: 0; transform: translateY(12px); }
.card-list-move                                  { transition: transform 0.3s cubic-bezier(0.4,0,0.2,1); }

/* ═══════════════════════════════════════════════════════
   MODAL (global)
   ═══════════════════════════════════════════════════════ */
.modal-overlay {
  position: fixed; inset: 0;
  background: rgba(30, 34, 58, 0.45);
  backdrop-filter: blur(6px);
  z-index: 9999;
  display: flex; align-items: center; justify-content: center;
  padding: 20px; overflow-y: auto;
}
.modal-card {
  background: #ffffff; border-radius: 20px;
  width: 100%; max-width: 580px; max-height: 90vh;
  display: flex; flex-direction: column; overflow: hidden;
  box-shadow: 0 20px 60px rgba(44, 51, 73, 0.18);
}
.modal-card--save-tpl    { max-width: 460px; }
.modal-card--manager     { max-width: 640px; }
.modal-card--picker      { max-width: 700px; } /* pour le dialogue */
.modal-card__head {
  display: flex; align-items: center; justify-content: space-between;
  padding: 18px 24px; border-bottom: 1px solid #eef0fc; gap: 12px; flex-shrink: 0;
}
.modal-card__brand { display: flex; align-items: center; gap: 12px; min-width: 0; }
.modal-card__icon {
  width: 44px; height: 44px; border-radius: 12px;
  display: flex; align-items: center; justify-content: center;
  font-size: 18px; flex-shrink: 0;
  background: #eef0fc; color: #5c6bc0;
}
.modal-card__icon--email    { background: #fce4ec; color: #ea4335; }
.modal-card__icon--whatsapp { background: #e9fbf0; color: #25d366; }
.modal-card__icon--webpush  { background: #fff3e0; color: #f4a84a; }
.modal-card__icon--chat     { background: #eef0fc; color: #5c6bc0; }
.modal-card__icon--tpl      { background: #f3e5f5; color: #6a1b9a; }
.modal-card__title    { font-size: 16px; font-weight: 800; color: #1a1a2e; line-height: 1.2; }
.modal-card__subtitle { font-size: 12px; color: #9aa0ba; margin-top: 2px; }
.modal-close {
  width: 36px; height: 36px; border: none; background: transparent;
  color: #9aa0ba; border-radius: 10px;
  display: flex; align-items: center; justify-content: center;
  cursor: pointer; font-size: 14px; transition: all 0.15s ease; flex-shrink: 0;
}
.modal-close:hover { background: #f4f6fb; color: #5c6bc0; }
.modal-card__body {
  flex: 1; min-height: 0; overflow-y: auto;
  padding: 22px 24px; display: flex; flex-direction: column; gap: 18px;
}
.modal-card__foot {
  display: flex; justify-content: flex-end; gap: 10px;
  padding: 14px 24px; border-top: 1px solid #eef0fc;
  background: #f9fafc; flex-shrink: 0;
}

/* ─── Split layout (Form + Preview) ─── */
.modal-body--split {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  align-items: start;
}
.modal-form-col { min-width: 0; }
.modal-preview-col { min-width: 0; }
.preview-section--sticky {
  position: sticky;
  top: 0;
}

/* ─── Use Template Button ─── */
.btn-use-template {
  display: flex; align-items: center; gap: 8px;
  width: 100%; padding: 10px 12px; border-radius: 10px;
  background: #f3e5f5; color: #6a1b9a;
  border: 1px solid #e1bee7; font-size: 13px; font-weight: 600;
  cursor: pointer; transition: all 0.15s ease;
}
.btn-use-template:hover { background: #e1bee7; }
.btn-use-template .tpl-badge {
  margin-left: auto;
  background: #6a1b9a; color: #fff;
  padding: 2px 8px; border-radius: 10px; font-size: 11px;
  font-weight: 700;
}

/* ─── Template Picker Dialog ─── */
.tpl-search--dialog {
  display: flex; align-items: center; gap: 8px;
  background: #f9fafc; border: 1.5px solid #e9ecf5;
  border-radius: 12px; padding: 0 14px;
  margin-bottom: 14px;
}
.tpl-search--dialog i { color: #9aa0ba; font-size: 13px; }
.tpl-search--dialog input {
  flex: 1; border: none; outline: none; padding: 10px 0;
  background: transparent; font-size: 13px; color: #2d3142;
}
.tpl-search--dialog input::placeholder { color: #b0b6cc; }

.tpl-picker-list {
  display: flex; flex-direction: column; gap: 10px;
  max-height: 55vh; overflow-y: auto; -webkit-overflow-scrolling: touch;
}
.tpl-picker-item {
  border: 1.5px solid #e9ecf5; border-radius: 12px;
  padding: 14px 16px; cursor: pointer; transition: all 0.15s ease;
  background: #fff;
}
.tpl-picker-item:hover { border-color: #6a1b9a; box-shadow: 0 2px 10px rgba(106,27,154,0.08); }
.tpl-picker-item__top {
  display: flex; align-items: center; justify-content: space-between; gap: 8px;
  margin-bottom: 6px;
}
.tpl-picker-item__subject {
  font-size: 12px; color: #6b7a99; margin: 0 0 4px;
  display: flex; align-items: center; gap: 5px;
}
.tpl-picker-item__body {
  font-size: 13px; line-height: 1.5; color: #2d3142;
  margin: 0 0 10px; white-space: pre-wrap; word-break: break-word;
}
.tpl-picker-item__footer {
  display: flex; align-items: center; justify-content: space-between;
}

/* ─── Channel picker ─── */
.channel-picker { display: flex; gap: 8px; flex-wrap: wrap; }
.channel-chip {
  display: inline-flex; align-items: center; gap: 7px; padding: 8px 16px;
  border-radius: 10px; font-size: 13px; font-weight: 700;
  border: 1.5px solid #e9ecf5; background: #f9fafc; color: #6b7a99;
  cursor: pointer; transition: all 0.18s cubic-bezier(0.4,0,0.2,1); outline: none;
}
.channel-chip:hover { border-color: #dde1f8; background: #f4f6fb; color: #5c6bc0; transform: translateY(-1px); }
.channel-chip--active {
  background: #eef0fc; border-color: #5c6bc0; color: #5c6bc0;
  box-shadow: 0 2px 8px rgba(92,107,192,0.12); transform: translateY(-1px);
}
.channel-chip i { font-size: 13px; }

/* ─── Template manager ─── */
.tpl-loading--big { padding: 32px; justify-content: center; font-size: 14px; }
.tpl-empty--big {
  flex-direction: column; align-items: center; justify-content: center;
  padding: 48px 20px; text-align: center; gap: 10px;
}
.tpl-empty--big i { font-size: 32px; opacity: 0.35; }
.tpl-empty--big p { font-size: 14px; font-weight: 600; color: #6b7a99; margin: 0; }
.tpl-empty--big small { font-size: 12px; color: #9aa0ba; }
.tpl-manager-list { display: flex; flex-direction: column; gap: 10px; }
.tpl-manager-item {
  border: 1.5px solid #e9ecf5; border-radius: 12px; padding: 14px 16px;
  background: #fff; transition: box-shadow 0.15s ease;
}
.tpl-manager-item:hover { box-shadow: 0 2px 10px rgba(44, 51, 73, 0.07); }
.tpl-manager-item__top { display: flex; align-items: center; justify-content: space-between; gap: 8px; margin-bottom: 6px; }
.tpl-manager-item__actions { display: flex; align-items: center; gap: 6px; flex-shrink: 0; }
.tpl-all-channels-badge {
  display: inline-flex; align-items: center; gap: 4px;
  padding: 2px 9px; border-radius: 10px; font-size: 10px; font-weight: 700;
  background: #f3e5f5; color: #6a1b9a; border: 1px solid #e1bee7;
}
.tpl-manager-item__subject { font-size: 12px; color: #6b7a99; font-weight: 500; margin: 0 0 4px; display: flex; align-items: center; gap: 5px; }
.tpl-manager-item__body { font-size: 13px; color: #2d3142; line-height: 1.5; margin: 0 0 10px; white-space: pre-wrap; word-break: break-word; }
.tpl-manager-item__footer { display: flex; align-items: center; justify-content: space-between; }
.tpl-manager-item__date { font-size: 11px; color: #9aa0ba; display: flex; align-items: center; gap: 5px; }
.tpl-compat-icons {
  display: flex; align-items: center; gap: 6px;
  font-size: 11px; color: #b0b6cc;
}
.tpl-compat-icons .fab.fa-whatsapp { color: #b2dfdb; }
.tpl-compat-icons .fas.fa-envelope  { color: #f8bbd0; }
.tpl-compat-icons .fas.fa-bell      { color: #ffe0b2; }
.tpl-compat-icons .fas.fa-comment-dots { color: #b2d8f0; }
.tpl-del-btn {
  width: 26px; height: 26px; border-radius: 8px; border: none;
  background: transparent; color: #d32f2f; cursor: pointer; font-size: 12px;
  display: flex; align-items: center; justify-content: center;
  transition: all 0.15s ease;
}
.tpl-del-btn:hover { background: #fce4ec; }

/* ─── Form elements ─── */
.form-group { display: flex; flex-direction: column; gap: 7px; }
.form-label {
  font-size: 12px; font-weight: 800; color: #1a1a2e;
  display: flex; justify-content: space-between; align-items: center;
}
.form-hint { font-size: 11px; color: #9aa0ba; font-weight: 500; }
.form-input, .form-textarea {
  border: 1.5px solid #e9ecf5; border-radius: 12px; padding: 11px 14px;
  font-family: inherit; font-size: 13px; color: #2d3142; background: #f9fafc;
  transition: border-color 0.2s, background 0.2s, box-shadow 0.2s;
  outline: none; width: 100%; box-sizing: border-box;
}
.form-input:focus, .form-textarea:focus {
  border-color: #5c6bc0; background: #ffffff;
  box-shadow: 0 0 0 3px rgba(92,107,192,0.08);
}
.form-textarea { resize: vertical; min-height: 110px; line-height: 1.6; }
.form-actions-row {
  display: flex; align-items: center; justify-content: space-between; gap: 8px;
}
.char-count      { text-align: right; font-size: 11px; color: #9aa0ba; font-weight: 600; }
.char-count--warn{ color: #e53935; font-weight: 800; }
.btn-save-tpl {
  display: inline-flex; align-items: center; gap: 5px;
  background: #f3e5f5; color: #6a1b9a;
  border: 1px solid #e1bee7; border-radius: 16px;
  padding: 4px 12px; font-size: 11px; font-weight: 600; cursor: pointer;
  transition: all 0.15s ease; white-space: nowrap;
}
.btn-save-tpl:hover { background: #e1bee7; }
.tpl-msg-preview {
  background: #f9fafc; border: 1.5px solid #e9ecf5; border-radius: 10px;
  padding: 10px 12px; font-size: 12px; color: #4a4f6a; line-height: 1.6;
  white-space: pre-wrap; word-break: break-word; max-height: 120px; overflow-y: auto;
}
.tpl-info-note {
  display: flex; align-items: flex-start; gap: 8px;
  background: #f3e5f5; color: #6a1b9a;
  border: 1px solid #e1bee7; border-radius: 10px;
  padding: 10px 12px; font-size: 12px; font-weight: 500; line-height: 1.5;
}
.tpl-info-note i { margin-top: 1px; flex-shrink: 0; }

/* ─── Preview ─── */
.preview-section { border-top: 1px solid #eef0fc; padding-top: 18px; display: flex; flex-direction: column; gap: 12px; }
.preview-header {
  display: flex; align-items: center; gap: 8px;
  font-size: 11px; font-weight: 800; color: #6b7a99;
  text-transform: uppercase; letter-spacing: 0.8px;
}
.preview-header i { color: #5c6bc0; font-size: 12px; }
.preview-channel {
  margin-left: auto; font-size: 10px; font-weight: 800;
  padding: 3px 10px; border-radius: 20px;
  background: #eef0fc; color: #5c6bc0;
  text-transform: none; letter-spacing: 0;
}

/* WhatsApp preview */
.preview-whatsapp { display: flex; justify-content: center; }
.wa-phone {
  width: 100%; max-width: 320px; background: #e5ddd5;
  border-radius: 16px; overflow: hidden;
  box-shadow: 0 4px 20px rgba(0,0,0,0.1); border: 1px solid #d1d1d1;
}
.wa-phone__header {
  background: #075e54; color: #fff; padding: 11px 16px;
  display: flex; align-items: center; gap: 8px; font-size: 13px; font-weight: 700;
}
.wa-phone__chat {
  padding: 18px 14px; min-height: 120px;
  display: flex; flex-direction: column; align-items: flex-end; gap: 8px;
  background-image: url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 0h100v100H0z' fill='%23e5ddd5'/%3E%3Cpath d='M0 0l100 100M100 0L0 100' stroke='%23d4d0c8' stroke-width='0.5' opacity='0.4'/%3E%3C/svg%3E");
}
.wa-bubble {
  background: #dcf8c6; border-radius: 12px 2px 12px 12px;
  padding: 10px 14px; max-width: 88%; box-shadow: 0 1px 3px rgba(0,0,0,0.08);
}
.wa-bubble__text  { font-size: 13px; line-height: 1.5; color: #1f2937; white-space: pre-wrap; word-break: break-word; }
.wa-bubble__meta  { display: flex; justify-content: flex-end; align-items: center; gap: 4px; margin-top: 4px; }
.wa-bubble__time  { font-size: 10px; color: #888; }
.wa-bubble__tick  { font-size: 10px; color: #4fc3f7; letter-spacing: -1px; }

/* Email preview */
.preview-email { display: flex; justify-content: center; }
.email-frame {
  width: 100%; max-width: 420px; background: #ffffff; border-radius: 12px;
  overflow: hidden; box-shadow: 0 2px 16px rgba(0,0,0,0.06); border: 1px solid #e9ecf5;
}
.email-frame__bar {
  background: #f4f6fb; padding: 9px 14px;
  display: flex; align-items: center; gap: 10px; border-bottom: 1px solid #e9ecf5;
}
.email-frame__dots { font-size: 8px; letter-spacing: 3px; color: #d1d5db; }
.email-frame__url {
  flex: 1; text-align: center; font-size: 11px; color: #9aa0ba;
  background: #fff; padding: 3px 12px; border-radius: 6px; border: 1px solid #e9ecf5;
}
.email-frame__body { padding: 18px; }
.email-header {
  display: flex; justify-content: space-between; align-items: flex-start;
  margin-bottom: 14px; padding-bottom: 14px; border-bottom: 1px solid #f0f2f8;
}
.email-header__from   { display: flex; align-items: center; gap: 10px; }
.email-avatar {
  width: 34px; height: 34px; border-radius: 50%; background: #5c6bc0; color: #fff;
  display: flex; align-items: center; justify-content: center; font-size: 11px; font-weight: 800;
}
.email-header__name   { font-size: 13px; font-weight: 700; color: #1a1a2e; }
.email-header__addr   { font-size: 11px; color: #9aa0ba; }
.email-header__date   { font-size: 11px; color: #9aa0ba; font-weight: 500; }
.email-subject { font-size: 14px; font-weight: 800; color: #1a1a2e; margin-bottom: 12px; }
.email-body    { font-size: 13px; line-height: 1.6; color: #4a4f6a; white-space: pre-wrap; word-break: break-word; }

/* WebPush preview */
.preview-webpush { display: flex; justify-content: center; }
.push-frame {
  width: 100%; max-width: 340px; background: #ffffff; border-radius: 16px; padding: 16px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.08); border: 1px solid #e9ecf5;
  display: flex; flex-direction: column; gap: 10px;
}
.push-frame__header { display: flex; justify-content: space-between; align-items: center; }
.push-app           { display: flex; align-items: center; gap: 8px; }
.push-app__icon {
  width: 30px; height: 30px; border-radius: 10px; background: #5c6bc0; color: #fff;
  display: flex; align-items: center; justify-content: center; font-size: 12px;
}
.push-app__name { font-size: 13px; font-weight: 700; color: #1a1a2e; }
.push-time      { font-size: 11px; color: #9aa0ba; font-weight: 600; }
.push-content   { display: flex; flex-direction: column; gap: 4px; }
.push-title     { font-size: 13px; font-weight: 800; color: #1a1a2e; }
.push-body      { font-size: 12px; line-height: 1.5; color: #6b7a99; white-space: pre-wrap; word-break: break-word; }

/* Chat preview */
.preview-chat { display: flex; justify-content: center; }
.chat-frame {
  width: 100%; max-width: 340px; background: #f0f2f5; border-radius: 16px;
  overflow: hidden; box-shadow: 0 4px 20px rgba(0,0,0,0.08); border: 1px solid #e4e6ea;
}
.chat-frame__header {
  background: #5c6bc0; padding: 12px 16px;
  display: flex; align-items: center; gap: 10px;
}
.chat-frame__avatar {
  width: 34px; height: 34px; background: rgba(255,255,255,0.2); border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  color: #fff; font-size: 14px; flex-shrink: 0;
}
.chat-frame__info  { display: flex; flex-direction: column; }
.chat-frame__name  { font-size: 13px; font-weight: 700; color: #fff; line-height: 1.2; }
.chat-frame__status{
  font-size: 11px; color: rgba(255,255,255,0.75);
  display: flex; align-items: center; gap: 5px; margin-top: 2px;
}
.chat-frame__dot {
  width: 7px; height: 7px; background: #4cff91;
  border-radius: 50%; display: inline-block;
}
.chat-frame__body {
  padding: 16px 12px; min-height: 100px;
  display: flex; flex-direction: column; align-items: flex-end; gap: 8px;
}
.chat-bubble--out {
  background: #5c6bc0; border-radius: 16px 4px 16px 16px;
  padding: 10px 14px; max-width: 88%;
  box-shadow: 0 1px 4px rgba(92,107,192,0.2);
}
.chat-bubble__text { font-size: 13px; line-height: 1.5; color: #fff; white-space: pre-wrap; word-break: break-word; }
.chat-bubble__meta { display: flex; justify-content: flex-end; align-items: center; gap: 5px; margin-top: 5px; }
.chat-bubble__time { font-size: 10px; color: rgba(255,255,255,0.65); }
.chat-bubble__tick { font-size: 10px; color: #a5d6ff; }

/* ─── Send buttons ─── */
.btn--send {
  background: #5c6bc0; color: #fff;
  box-shadow: 0 2px 10px rgba(92,107,192,0.25);
}
.btn--send:hover:not(:disabled)      { background: #4a5ab5; transform: translateY(-1px); }
.btn--send--whatsapp                 { background: #25d366; box-shadow: 0 2px 10px rgba(37,211,102,0.25); }
.btn--send--whatsapp:hover:not(:disabled) { background: #1ebe5a; }
.btn--send--email                    { background: #ea4335; box-shadow: 0 2px 10px rgba(234,67,53,0.2); }
.btn--send--email:hover:not(:disabled)    { background: #d33b2c; }
.btn--send--webpush                  { background: #f4a84a; box-shadow: 0 2px 10px rgba(244,168,74,0.2); }
.btn--send--webpush:hover:not(:disabled)  { background: #e0983d; }
.btn--send--chat                     { background: #5c6bc0; box-shadow: 0 2px 10px rgba(92,107,192,0.25); }
.btn--send--chat:hover:not(:disabled)     { background: #4a5ab5; }
.btn--send:disabled { opacity: 0.45; cursor: not-allowed; transform: none; box-shadow: none; }
.btn--ghost {
  background: transparent; border: 1.5px solid #e9ecf5; color: #6b7a99;
}
.btn--ghost:hover { background: #f4f6fb; color: #5c6bc0; border-color: #dde1f8; }
.btn--save-confirm {
  background: #6a1b9a;
  color: #fff;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: all 0.18s ease;
  box-shadow: 0 2px 8px rgba(106, 27, 154, 0.25);
}
.btn--save-confirm:hover:not(:disabled) { opacity: 0.9; transform: translateY(-1px); }
.btn--save-confirm:disabled { opacity: 0.5; cursor: not-allowed; }

/* ─── Transitions ─── */
.modal-enter-active, .modal-leave-active { transition: opacity 0.25s ease; }
.modal-enter, .modal-leave-to           { opacity: 0; }
.modal-enter-active .modal-card, .modal-leave-active .modal-card {
  transition: transform 0.3s cubic-bezier(0.16,1,0.3,1), opacity 0.25s ease;
}
.modal-enter .modal-card, .modal-leave-to .modal-card {
  transform: scale(0.95) translateY(12px); opacity: 0;
}
.field-slide-enter-active, .field-slide-leave-active {
  transition: all 0.22s ease;
  max-height: 200px;
  opacity: 1;
  overflow: hidden;
}
.field-slide-enter, .field-slide-leave-to {
  max-height: 0;
  opacity: 0;
}

/* ═══════════════════════════════════════════════════════
   NOUVEAUX STYLES (Template creation)
   ═══════════════════════════════════════════════════════ */
.btn-create-template,
.btn--create-template-manager {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border-radius: 10px;
  background: #f3e5f5;
  color: #6a1b9a;
  border: 1px solid #e1bee7;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;
}
.btn-create-template:hover,
.btn--create-template-manager:hover {
  background: #e1bee7;
}

.btn--create-template-manager {
  margin-right: 8px;
}

.modal-card__head-actions {
  display: flex;
  align-items: center;
  gap: 6px;
}

/* ═══════════════════════════════════════════════════════
   RESPONSIVE
   ═══════════════════════════════════════════════════════ */
@media (max-width: 1100px) {
  .stats-grid { grid-template-columns: repeat(2, 1fr); }
}
@media (max-width: 900px) {
  .zone-bar__inner { flex-direction: column; align-items: stretch; gap: 12px; }
  .zone-picker, .date-picker { max-width: 100%; flex: none; }
  .zone-divider { display: none; }
  .btn--search { align-self: flex-start; }
}
@media (max-width: 768px) {
  .dash-root { padding: 12px; }
  .dash-hero__inner { flex-direction: column; align-items: stretch; padding: 18px 20px 14px; }
  .dash-hero__actions { justify-content: flex-start; }
  .zone-bar__inner { padding: 12px 20px; }
  .stats-grid { grid-template-columns: repeat(2, 1fr); gap: 12px; }
  .stat-card { padding: 14px 16px; }
  .stat-card__val { font-size: 18px; }
  .table-toolbar { padding: 12px 16px; }
  .search-group { max-width: 100%; }
  .modal-overlay { padding: 0; align-items: flex-end; }
  .modal-card { border-radius: 20px 20px 0 0; max-height: 92vh; }
  .modal-card__head, .modal-card__body, .modal-card__foot { padding-left: 20px; padding-right: 20px; }
  .modal-body--split { grid-template-columns: 1fr; }
}
@media (max-width: 480px) {
  .stats-grid { grid-template-columns: 1fr; }
  .dash-hero__title { font-size: 18px; }
  .dash-hero__icon  { width: 40px; height: 40px; font-size: 16px; }
  .btn { padding: 8px 14px; font-size: 12px; }
  .btn__text { display: none; }
  .btn--search .btn__text { display: inline; }
  .table-meta { font-size: 12px; }
  .mobile-card__actions { gap: 4px; }
  .mobile-action { padding: 8px 2px; font-size: 9px; }
  .mobile-action i { font-size: 13px; }
}
@media (max-width: 360px) {
  .dash-hero__inner   { padding: 14px 16px 10px; }
  .zone-bar__inner    { padding: 10px 16px; }
  .date-picker        { flex-wrap: wrap; }
  .date-sep           { display: none; }
  .btn--search        { width: 100%; justify-content: center; }
  .mobile-card__header{ padding: 12px 12px 10px; }
  .mobile-card__body  { padding: 10px 12px; }
  .mobile-card__actions{ padding: 10px 12px 12px; }
}
</style>
